import React, { useState, useEffect, useContext } from 'react';
import { Container, Box, Typography, TextField, Button, Alert, Grid, Paper, InputAdornment, IconButton, Menu, MenuItem } from '@mui/material';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import Visibility from '@mui/icons-material/Visibility';
import FlagIcon from '../components/ui/FlagIcon';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Account = () => {
  // State for user info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+32');
  const [localPhone, setLocalPhone] = useState('');
  const [infoError, setInfoError] = useState('');
  const [infoSuccess, setInfoSuccess] = useState('');

  // State for password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const { currentUser, setCurrentUser } = useContext(AuthContext);

  // State for password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleFlagMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFlagMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePrefixSelect = (prefix) => {
    setPhonePrefix(prefix);
    handleFlagMenuClose();
  };

  // Populate fields on component mount
  useEffect(() => {
    if (currentUser) {
      setFirstName(currentUser.firstName);
      setLastName(currentUser.lastName);
      setEmail(currentUser.email);
      const fullPhone = currentUser.phone || '';
      if (fullPhone.startsWith('+33')) {
        setPhonePrefix('+33');
        setLocalPhone('0' + fullPhone.substring(3));
      } else { // Default to Belgian prefix if it's not French or is empty/malformed
        setPhonePrefix('+32');
        const localPart = fullPhone.startsWith('+32') ? fullPhone.substring(3) : fullPhone;
        setLocalPhone(localPart ? '0' + localPart : '');
      }
    }
  }, [currentUser]);

  // Handle info update
  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    setInfoError('');
    setInfoSuccess('');
    
    if (!firstName || !lastName || !localPhone || !firstName.trim() || !lastName.trim() || !localPhone.trim()) {
      setInfoError('Le prénom, le nom et le numéro de téléphone sont requis.');
      return;
    }

    const cleanedLocalPhone = localPhone.replace(/\s/g, '');
    let fullPhone = cleanedLocalPhone;

    if (!cleanedLocalPhone.startsWith(phonePrefix)) {
      if (cleanedLocalPhone.startsWith('0') && (phonePrefix === '+32' || phonePrefix === '+33')) {
        fullPhone = phonePrefix + cleanedLocalPhone.substring(1);
      } else {
        fullPhone = phonePrefix + cleanedLocalPhone;
      }
    }

    try {
      const response = await axiosInstance.post('/me/edit', { firstName, lastName, phone: fullPhone });
      const currentUserClone = { ...currentUser };
      currentUserClone.firstName = response.data.firstName;
      currentUserClone.lastName = response.data.lastName;
      currentUserClone.phone = response.data.phone;
      setCurrentUser(currentUserClone); // Update context
      setInfoSuccess('Informations mises à jour avec succès.');
    } catch (err) {
      setInfoError('Une erreur est survenue lors de la mise à jour.');
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    try {
      await axiosInstance.post('/me/password', { currentPassword, newPassword });
      setPasswordSuccess('Mot de passe modifié avec succès.');
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      const errorCode = err.response?.data?.error;
      switch (errorCode) {
        case 'INVALID_CURRENT_PASSWORD':
          setPasswordError('Le mot de passe actuel est incorrect.');
          break;
        case 'WEAK_PASSWORD':
          setPasswordError('Le nouveau mot de passe est trop faible.');
          break;
        default:
          setPasswordError('Une erreur est survenue lors de la modification.');
      }
    }
  };

  const isIncomplete = !currentUser || !currentUser.firstName || !currentUser.lastName || !currentUser.phone;

  return (
    <Container maxWidth="md">
      {isIncomplete && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Veuillez compléter vos informations personnelles (prénom, nom et numéro de téléphone) pour pouvoir accéder au reste du site.
        </Alert>
      )}
      <Grid container spacing={4}>
        {/* Personal Information Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations personnelles
            </Typography>
            <Box component="form" onSubmit={handleInfoSubmit}>
              {infoError && <Alert severity="error" sx={{ mb: 2 }}>{infoError}</Alert>}
              {infoSuccess && <Alert severity="success" sx={{ mb: 2 }}>{infoSuccess}</Alert>}
              <TextField
                label="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Numéro de téléphone"
                value={localPhone}
                onChange={(e) => setLocalPhone(e.target.value)}
                fullWidth
                required
                margin="normal"
                helperText={
                  phonePrefix === '+32' ? 'Ex: 0470123456' :
                  'Ex: 0612345678)'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Button onClick={handleFlagMenuOpen} sx={{ minWidth: 'auto', padding: '6px' }}>
                        <FlagIcon countryCode={phonePrefix === '+32' ? 'BE' : 'FR'} />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleFlagMenuClose}
              >
                <MenuItem onClick={() => handlePrefixSelect('+32')}>
                  <FlagIcon countryCode="BE" sx={{ mr: 1.5 }} /> Belgique (+32)
                </MenuItem>
                <MenuItem onClick={() => handlePrefixSelect('+33')}>
                  <FlagIcon countryCode="FR" sx={{ mr: 1.5 }} /> France (+33)
                </MenuItem>
              </Menu>
              <TextField
                label="Adresse Email"
                type="email"
                value={email}
                fullWidth
                margin="normal"
                disabled
              />
              <Button type="submit" variant="contained" sx={{ mt: 2 }}>
                Enregistrer les modifications
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Change Password Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Changer le mot de passe
            </Typography>
            <Box component="form" onSubmit={handlePasswordSubmit}>
              {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
              {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}
              <TextField
                label="Mot de passe actuel"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={handleClickShowCurrentPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="start"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Nouveau mot de passe"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="start"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirmer le nouveau mot de passe"
                type={showConfirmNewPassword ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        aria-label="toggle confirm new password visibility"
                        onClick={handleClickShowConfirmNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="start"
                      >
                        {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" color="secondary" sx={{ mt: 2 }}>
                Changer le mot de passe
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Account;